<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import FileUpload from 'vue-upload-component';
import { DirectUpload } from 'activestorage';

import { ALLOWED_FILE_TYPES } from 'shared/constants/messages';
import {
  checkFileSizeLimit,
  isFileTypeAllowedForChannel,
  resolveMaximumFileUploadSize,
} from 'shared/helpers/FileHelper';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import CannedResponseAPI from 'dashboard/api/cannedResponse';
import AttachmentPreview from 'dashboard/components/widgets/AttachmentsPreview.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  existingAttachments: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits([
  'update:isUploading',
  'update:retainedAttachmentIds',
  'update:uploadedBlobSignedIds',
]);

const { t } = useI18n();
const currentUser = useMapGetter('getCurrentUser');
const globalConfig = useMapGetter('globalConfig/get');
const attachments = ref([]);
const pendingUploadCount = ref(0);

const normalizeExistingAttachment = attachment => {
  const filename = attachment.filename || attachment.name;
  const contentType = attachment.content_type || attachment.type;
  const byteSize = attachment.byte_size || attachment.size;

  return {
    id: attachment.id,
    resource: {
      id: attachment.id,
      name: filename,
      filename,
      type: contentType,
      content_type: contentType,
      size: byteSize,
      byte_size: byteSize,
    },
    thumb: attachment.preview_url,
    reusableAttachmentId: attachment.id,
    blobSignedId: undefined,
    currentChatId: null,
    isPrivate: false,
  };
};

const retainedAttachmentIds = computed(() =>
  attachments.value
    .map(attachment => attachment.reusableAttachmentId)
    .filter(Boolean)
);

const uploadedBlobSignedIds = computed(() =>
  attachments.value
    .filter(attachment => !attachment.reusableAttachmentId)
    .map(attachment => attachment.blobSignedId)
    .filter(Boolean)
);

const isUploading = computed(() => pendingUploadCount.value > 0);

const maximumUploadSize = computed(() =>
  resolveMaximumFileUploadSize(globalConfig.value?.maximumFileUploadSize)
);

watch(
  () => props.existingAttachments,
  value => {
    attachments.value = value.map(normalizeExistingAttachment);
  },
  { deep: true, immediate: true }
);

watch(
  retainedAttachmentIds,
  value => emit('update:retainedAttachmentIds', value),
  { immediate: true }
);

watch(
  uploadedBlobSignedIds,
  value => emit('update:uploadedBlobSignedIds', value),
  { immediate: true }
);

watch(isUploading, value => emit('update:isUploading', value), {
  immediate: true,
});

const alertFileSizeLimit = () => {
  useAlert(
    t('CONVERSATION.FILE_SIZE_LIMIT', {
      MAXIMUM_SUPPORTED_FILE_UPLOAD_SIZE: maximumUploadSize.value,
    })
  );
};

const appendUploadedAttachment = ({ file, blob, thumb }) => {
  attachments.value = [
    ...attachments.value,
    {
      id: blob.signed_id || blob.id,
      resource: {
        id: blob.id,
        name: blob.filename || file.name,
        filename: blob.filename || file.name,
        type: blob.content_type || file.type,
        content_type: blob.content_type || file.type,
        size: blob.byte_size || file.size,
        byte_size: blob.byte_size || file.size,
      },
      thumb,
      reusableAttachmentId: undefined,
      blobSignedId: blob.signed_id,
      currentChatId: null,
      isPrivate: false,
    },
  ];
};

const readFilePreview = ({ file, blob }) => {
  const reader = new FileReader();

  reader.onload = () => {
    appendUploadedAttachment({ file, blob, thumb: reader.result });
    pendingUploadCount.value -= 1;
    useAlert(t('CANNED_MGMT.ATTACHMENTS.UPLOAD_SUCCESS'));
  };

  reader.onerror = () => {
    pendingUploadCount.value -= 1;
    useAlert(t('CANNED_MGMT.ATTACHMENTS.UPLOAD_ERROR'));
  };

  reader.readAsDataURL(file);
};

const onFileUpload = newFile => {
  if (!newFile?.file) return;

  if (
    !isFileTypeAllowedForChannel(newFile.file, {
      isOnPrivateNote: true,
    })
  ) {
    useAlert(
      t('CONVERSATION.FILE_TYPE_NOT_SUPPORTED', {
        fileName: newFile.file.name,
      })
    );
    return;
  }

  if (!checkFileSizeLimit(newFile, maximumUploadSize.value)) {
    alertFileSizeLimit();
    return;
  }

  useAlert(t('CANNED_MGMT.ATTACHMENTS.UPLOADING'));
  pendingUploadCount.value += 1;

  const upload = new DirectUpload(
    newFile.file,
    CannedResponseAPI.directUploadsUrl(),
    {
      directUploadWillCreateBlobWithXHR: xhr => {
        xhr.setRequestHeader(
          'api_access_token',
          currentUser.value.access_token
        );
      },
    }
  );

  upload.create((error, blob) => {
    if (error) {
      pendingUploadCount.value -= 1;
      useAlert(t('CANNED_MGMT.ATTACHMENTS.UPLOAD_ERROR'));
      return;
    }

    readFilePreview({ file: newFile.file, blob });
  });
};

const removeAttachment = updatedAttachments => {
  attachments.value = updatedAttachments;
};
</script>

<template>
  <div class="space-y-2">
    <div class="flex justify-start">
      <FileUpload
        input-id="cannedResponseMediaAttachment"
        :accept="ALLOWED_FILE_TYPES"
        :size="maximumUploadSize * 1024 * 1024"
        multiple
        :drop-directory="false"
        :data="{
          direct_upload_url: CannedResponseAPI.directUploadsUrl(),
          direct_upload: true,
        }"
        class="p-px"
        @input-file="onFileUpload"
      >
        <Button
          type="button"
          icon="i-lucide-paperclip"
          color="slate"
          size="sm"
          class="!w-auto !px-3"
          :label="t('CANNED_MGMT.ATTACHMENTS.ATTACH_MEDIA')"
        />
      </FileUpload>
    </div>
    <p class="text-xs text-n-slate-11">
      {{ t('CANNED_MGMT.ATTACHMENTS.HELP') }}
    </p>
    <AttachmentPreview
      v-if="attachments.length"
      :attachments="attachments"
      @remove-attachment="removeAttachment"
    />
  </div>
</template>
